import { useState, useEffect, useCallback } from 'react';
import {
  FileText, ShoppingCart, Plus, Trash, Check, X, CircleNotch,
  Warning, Package,
  User, EnvelopeSimple, Buildings, Phone
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TranslateButton } from '../components/TranslateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUOTE_STATUS = {
  concept: { bg: 'bg-zinc-100 text-zinc-600', label: 'Concept' },
  verzonden: { bg: 'bg-blue-100 text-blue-700', label: 'Verzonden' },
  geaccepteerd: { bg: 'bg-emerald-100 text-emerald-700', label: 'Geaccepteerd' },
  afgewezen: { bg: 'bg-red-100 text-red-700', label: 'Afgewezen' },
};
const ORDER_STATUS = {
  concept: { bg: 'bg-zinc-100 text-zinc-600', label: 'Concept' },
  bevestigd: { bg: 'bg-blue-100 text-blue-700', label: 'Bevestigd' },
  naar_exact: { bg: 'bg-purple-100 text-purple-700', label: 'Naar Exact' },
  afgerond: { bg: 'bg-emerald-100 text-emerald-700', label: 'Afgerond' },
};

function ItemsEditor({ items, onChange }) {
  const addItem = () => onChange([...items, { description: '', quantity: 1, article_number: '' }]);
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: value };
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-500 px-1">
        <div className="col-span-3">Artikelnr</div>
        <div className="col-span-7">Omschrijving</div>
        <div className="col-span-1 text-center">Aantal</div>
        <div className="col-span-1"></div>
      </div>
      {items.map((item, i) => {
        const unmatched = !item.article_number;
        return (
          <div
            key={i}
            data-testid={`item-row-${i}`}
            className={`grid grid-cols-12 gap-2 items-center p-1 rounded-sm ${unmatched ? 'bg-yellow-50 border border-yellow-300' : ''}`}
          >
            <Input
              className={`col-span-3 rounded-sm text-sm h-8 font-data ${unmatched ? 'bg-yellow-100/50 border-yellow-300' : ''}`}
              value={item.article_number || ''}
              onChange={e => updateItem(i, 'article_number', e.target.value)}
              placeholder={unmatched ? 'Geen match — vul EOL artikelnr' : 'Art.nr'}
            />
            <Input
              className="col-span-7 rounded-sm text-sm h-8"
              value={item.description}
              onChange={e => updateItem(i, 'description', e.target.value)}
              placeholder="Omschrijving"
            />
            <Input
              className="col-span-1 rounded-sm text-sm h-8 font-data text-center"
              type="number" min="1"
              value={item.quantity}
              onChange={e => updateItem(i, 'quantity', parseInt(e.target.value) || 0)}
            />
            <button onClick={() => removeItem(i)} className="col-span-1 p-1 hover:bg-red-50 rounded-sm flex justify-center">
              <Trash size={14} className="text-zinc-400 hover:text-red-500" />
            </button>
          </div>
        );
      })}
      <button onClick={addItem} className="flex items-center gap-1 text-xs text-[#0055FF] hover:underline mt-1"><Plus size={12} /> Artikel toevoegen</button>
    </div>
  );
}

function DetailPanel({ item, type, onClose, onSave }) {
  const [editItems, setEditItems] = useState(item?.items || []);
  const [editStatus, setEditStatus] = useState(item?.status || 'concept');
  const [editNotes, setEditNotes] = useState(item?.notes || '');
  const [saving, setSaving] = useState(false);
  const statuses = type === 'quote' ? QUOTE_STATUS : ORDER_STATUS;

  const save = async () => {
    setSaving(true);
    const endpoint = type === 'quote' ? `quotes/${item.quote_id}` : `orders/${item.order_id}`;
    await fetch(`${API}/${endpoint}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: editStatus, items: editItems, notes: editNotes }),
    });
    setSaving(false);
    onSave();
  };

  if (!item) return null;
  const unmatchedCount = editItems.filter(i => !i.article_number).length;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl animate-slide-in-right" onClick={e => e.stopPropagation()} data-testid="detail-panel">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <span className="font-heading text-base font-semibold">{item.reference}</span>
            <Badge className={`rounded-sm px-2 py-0.5 text-xs ${(statuses[item.status] || statuses.concept).bg}`}>{(statuses[item.status] || statuses.concept).label}</Badge>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-sm"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          {/* Customer */}
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Klantgegevens</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><User size={12} className="inline mr-1 text-zinc-400" />{item.customer?.name || '-'}</div>
              <div><Buildings size={12} className="inline mr-1 text-zinc-400" />{item.customer?.company || '-'}</div>
              <div><EnvelopeSimple size={12} className="inline mr-1 text-zinc-400" />{item.customer?.email || '-'}</div>
              <div><Phone size={12} className="inline mr-1 text-zinc-400" />{item.customer?.phone || '-'}</div>
            </div>
          </div>
          {/* AI Summary */}
          {item.ai_summary && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-sm text-blue-800">
              <span className="text-xs font-medium text-blue-600">AI Samenvatting: </span>{item.ai_summary}
            </div>
          )}
          {/* Items */}
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Artikelen</div>
              {unmatchedCount > 0 && (
                <div className="text-[11px] flex items-center gap-1 px-2 py-0.5 bg-yellow-100 border border-yellow-300 text-yellow-900 rounded-sm" data-testid="unmatched-count-badge">
                  <Warning size={11} weight="fill" /> {unmatchedCount} regel{unmatchedCount > 1 ? 's' : ''} zonder catalogus-match
                </div>
              )}
            </div>
            <ItemsEditor items={editItems} onChange={setEditItems} />
            <div className="mt-3 text-[11px] text-zinc-400 italic">
              Naar Exact Online wordt enkel artikelnummer + aantal verzonden. Geen prijsinformatie.
            </div>
          </div>
          {/* Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Status</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(statuses).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Notities</label>
              <Input value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Interne notities" className="rounded-sm text-sm" />
            </div>
          </div>
          {/* Original email */}
          {item.original_text && (
            <OriginalEmailWithTranslate text={item.original_text} />
          )}
          {/* Save */}
          <button onClick={save} disabled={saving} data-testid="save-detail-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={16} />}
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OriginalEmailWithTranslate({ text }) {
  const [translated, setTranslated] = useState('');
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-zinc-500">Originele email</span>
        <TranslateButton text={text} onTranslated={(t) => setTranslated(t)} size="sm" />
      </div>
      {translated ? (
        <div>
          <div className="text-xs text-zinc-600 whitespace-pre-wrap max-h-[60vh] overflow-y-auto">{translated}</div>
          <div className="mt-1 text-[10px] text-indigo-500 cursor-pointer hover:underline" onClick={() => setTranslated('')}>Toon origineel</div>
        </div>
      ) : (
        <div className="text-xs text-zinc-600 whitespace-pre-wrap max-h-[60vh] overflow-y-auto">{text}</div>
      )}
    </div>
  );
}

function DocTable({ items, type, onSelect, onDelete }) {
  const statuses = type === 'quote' ? QUOTE_STATUS : ORDER_STATUS;
  const idField = type === 'quote' ? 'quote_id' : 'order_id';
  return (
    <div className="border border-zinc-200 overflow-hidden" data-testid={`${type}s-table`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-zinc-50 border-b border-zinc-200">
            <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Referentie</th>
            <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Klant</th>
            <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Onderwerp</th>
            <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Artikelen</th>
            <th className="text-center px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Match</th>
            <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Status</th>
            <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Datum</th>
            <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Acties</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-400 text-xs">
              {type === 'quote' ? <FileText size={24} className="mx-auto mb-2 text-zinc-300" /> : <ShoppingCart size={24} className="mx-auto mb-2 text-zinc-300" />}
              Geen {type === 'quote' ? 'offertes' : 'orders'}.
            </td></tr>
          ) : items.map(d => {
            const st = statuses[d.status] || statuses.concept;
            const totalLines = d.items?.length || 0;
            const unmatchedLines = (d.items || []).filter(i => !i.article_number).length;
            return (
              <tr key={d[idField]} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => onSelect(d)} data-testid={`row-${d[idField]}`}>
                <td className="px-4 py-2.5"><div className="font-data text-sm font-medium">{d.reference}</div></td>
                <td className="px-4 py-2.5"><div className="text-sm">{d.customer?.name || d.customer?.company || '-'}</div><div className="text-[10px] text-zinc-400">{d.customer?.email}</div></td>
                <td className="px-4 py-2.5 text-sm max-w-[180px] truncate">{d.subject || '-'}</td>
                <td className="px-4 py-2.5 text-center font-data">{totalLines}</td>
                <td className="px-4 py-2.5 text-center">
                  {totalLines === 0 ? (
                    <span className="text-[10px] text-zinc-400">—</span>
                  ) : unmatchedLines === 0 ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-emerald-100 text-emerald-700 font-bold">✓ Volledig</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm bg-yellow-200 text-yellow-900 border border-yellow-300 font-bold" data-testid={`unmatched-${d[idField]}`}>
                      <Warning size={9} weight="fill" /> {unmatchedLines} open
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5"><Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${st.bg}`}>{st.label}</Badge></td>
                <td className="px-4 py-2.5 text-xs text-zinc-500">{d.created_at ? new Date(d.created_at).toLocaleDateString('nl-NL') : '-'}</td>
                <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                  <button onClick={() => onDelete(d[idField])} className="p-1 hover:bg-red-50 rounded-sm"><Trash size={14} className="text-zinc-400 hover:text-red-500" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function QuotesOrdersPage({ embedded = false, view = 'all' }) {
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quoteStats, setQuoteStats] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedType, setSelectedType] = useState('quote');

  const fetchAll = useCallback(() => {
    Promise.all([
      fetch(`${API}/quotes`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/orders`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/quotes/stats`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/orders/stats`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([q, o, qs, os]) => {
      setQuotes(q.quotes || []); setOrders(o.orders || []);
      setQuoteStats(qs); setOrderStats(os);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteDoc = async (type, id) => {
    await fetch(`${API}/${type}s/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchAll();
  };

  const openDetail = (item, type) => { setSelected(item); setSelectedType(type); };
  const closeDetail = () => setSelected(null);
  const onSave = () => { closeDetail(); fetchAll(); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="quotes-orders-page">
      {!embedded && (
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-black tracking-tight">Offertes & Orders</h1>
          <p className="text-sm text-zinc-500 mt-1">Automatisch aangemaakt uit inkomende emails, klaar voor Exact Online</p>
        </div>
      )}

      {view === 'all' ? (
      <Tabs defaultValue="quotes">
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto mb-6">
          {['quotes', 'orders'].map(tab => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm">
              {tab === 'quotes' ? (
                <span className="flex items-center gap-2">
                  <FileText size={14} /> Offertes
                  {quoteStats?.concept > 0 && <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-amber-500 text-white rounded-full">{quoteStats.concept}</span>}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShoppingCart size={14} /> Orders
                  {orderStats?.concept > 0 && <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-amber-500 text-white rounded-full">{orderStats.concept}</span>}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="quotes" className="mt-0">
          {quoteStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="quote-stats">
              {[
                { l: 'Totaal', v: quoteStats.total, c: 'text-zinc-700' },
                { l: 'Concept', v: quoteStats.concept, c: quoteStats.concept > 0 ? 'text-amber-600' : 'text-zinc-400' },
                { l: 'Verzonden', v: quoteStats.verzonden, c: 'text-blue-600' },
                { l: 'Geaccepteerd', v: quoteStats.geaccepteerd, c: 'text-emerald-600' },
                { l: 'Afgewezen', v: quoteStats.afgewezen, c: 'text-red-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}
          <DocTable items={quotes} type="quote" onSelect={d => openDetail(d, 'quote')} onDelete={id => deleteDoc('quote', id)} />
        </TabsContent>

        <TabsContent value="orders" className="mt-0">
          {orderStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="order-stats">
              {[
                { l: 'Totaal', v: orderStats.total, c: 'text-zinc-700' },
                { l: 'Concept', v: orderStats.concept, c: orderStats.concept > 0 ? 'text-amber-600' : 'text-zinc-400' },
                { l: 'Bevestigd', v: orderStats.bevestigd, c: 'text-blue-600' },
                { l: 'Naar Exact', v: orderStats.naar_exact, c: 'text-purple-600' },
                { l: 'Afgerond', v: orderStats.afgerond, c: 'text-emerald-600' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}
          <DocTable items={orders} type="order" onSelect={d => openDetail(d, 'order')} onDelete={id => deleteDoc('order', id)} />
        </TabsContent>
      </Tabs>
      ) : view === 'quotes' ? (
        <>
          {quoteStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="quote-stats">
              {[
                { l: 'Totaal', v: quoteStats.total, c: 'text-zinc-700' },
                { l: 'Concept', v: quoteStats.concept, c: 'text-amber-600' },
                { l: 'Verstuurd', v: quoteStats.verstuurd, c: 'text-blue-600' },
                { l: 'Geaccepteerd', v: quoteStats.geaccepteerd, c: 'text-emerald-600' },
                { l: 'Afgewezen', v: quoteStats.afgewezen, c: 'text-red-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}
          <DocTable items={quotes} type="quote" onSelect={d => openDetail(d, 'quote')} onDelete={id => deleteDoc('quote', id)} />
        </>
      ) : (
        <>
          {orderStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="order-stats">
              {[
                { l: 'Totaal', v: orderStats.total, c: 'text-zinc-700' },
                { l: 'Concept', v: orderStats.concept, c: 'text-amber-600' },
                { l: 'Verwerkt', v: orderStats.verwerkt, c: 'text-blue-600' },
                { l: 'Verzonden', v: orderStats.verzonden, c: 'text-emerald-600' },
                { l: 'Geannuleerd', v: orderStats.geannuleerd, c: 'text-red-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}
          <DocTable items={orders} type="order" onSelect={d => openDetail(d, 'order')} onDelete={id => deleteDoc('order', id)} />
        </>
      )}

      <div className="mt-6 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-800 flex items-center gap-2">
        <Package size={16} className="shrink-0" />
        <span>Offertes en orders worden <strong>automatisch aangemaakt</strong> wanneer inkomende emails worden geclassificeerd als offerte-aanvraag of bestelling.</span>
      </div>

      {selected && <DetailPanel item={selected} type={selectedType} onClose={closeDetail} onSave={onSave} />}
    </div>
  );
}
